import { forwardRef, HTMLAttributes, KeyboardEvent, MouseEvent, ReactNode } from 'react'

interface SpanButtonProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'onClick' | 'role' | 'tabIndex' | 'onKeyDown'> {
  onClick: (e: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>) => void
  children: ReactNode
  'data-event-category'?: string
  'data-event-action'?: string
  'data-event-label'?: string
}

// Renders as an inline <span>, not a native <button>: browsers treat
// <button> as an atomic, non-fragmentable inline box (confirmed via
// getClientRects()), so once a button's own text is long enough to wrap
// internally, it consumes the full remaining line width and strands
// whatever sits directly after it (e.g. a closing bracket) on its own line.
// A <span role="button"> wraps like ordinary text instead. See
// SmartFcLegislationReference.tsx for the case that surfaced this.
const SpanButton = forwardRef<HTMLSpanElement, SpanButtonProps>(
  ({ onClick, children, ...rest }, ref) => (
    <span
      ref={ref}
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick(e)
        }
      }}
      {...rest}
    >
      {children}
    </span>
  )
)

SpanButton.displayName = 'SpanButton'

export default SpanButton
